/**
 * 团队服务：创建团队、我的团队、成员邀请/列表/更新、计费模式切换。
 *
 * @module services/team
 */
import type { BillingMode, Team, TeamMember, TeamRole } from '@/types';
import { API } from '@/config/api';
import { request } from './http';

/** 我的团队（可能附带成员列表）。 */
export type TeamWithMembers = Team & { members?: TeamMember[] };

/** 创建团队入参。 */
export interface CreateTeamInput {
  name: string;
  billingMode: BillingMode;
}

/** 邀请成员入参。 */
export interface InviteMemberInput {
  userId: number;
  role?: TeamRole;
  quotaLimit?: string | null;
}

/** 更新成员入参。 */
export interface UpdateMemberInput {
  quotaLimit?: string | null;
  role?: TeamRole;
}

/** 创建团队。 */
export async function createTeam(input: CreateTeamInput): Promise<Team> {
  return request<Team>({ method: 'POST', url: API.team.create, data: input });
}

/** 获取我的团队（可能包含 members）。 */
export async function getMyTeam(): Promise<TeamWithMembers> {
  return request<TeamWithMembers>({ method: 'GET', url: API.team.mine });
}

/** 邀请成员。 */
export async function inviteMember(
  teamId: number,
  input: InviteMemberInput,
): Promise<TeamMember> {
  return request<TeamMember>({
    method: 'POST',
    url: API.team.members(teamId),
    data: input,
  });
}

/** 列出团队成员。 */
export async function listMembers(teamId: number): Promise<TeamMember[]> {
  return request<TeamMember[]>({ method: 'GET', url: API.team.members(teamId) });
}

/** 更新成员额度/角色。 */
export async function updateMember(
  teamId: number,
  memberId: number,
  input: UpdateMemberInput,
): Promise<TeamMember> {
  return request<TeamMember>({
    method: 'PATCH',
    url: API.team.memberDetail(teamId, memberId),
    data: input,
  });
}

/** 更新团队计费模式。 */
export async function updateBillingMode(
  teamId: number,
  billingMode: BillingMode,
): Promise<Team> {
  return request<Team>({
    method: 'PATCH',
    url: API.team.billingMode(teamId),
    data: { billingMode },
  });
}
